import React from "react"
import { Link } from "react-router-dom"
import {
  MapPin,
  Phone,
  Mail,
  Instagram,
  Linkedin,
  Facebook,
  ChevronRight,
  Globe,
  HeartPulse,
  Award
} from "lucide-react"
import { INSTITUTES } from "../constants"

const Footer = () => {
  return (
    <footer className="bg-white text-slate-900 pt-24 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6">
        {/* --- MAIN DIRECTORY GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-16 mb-20">
          {/* Identity & Mission (3/12) */}
          <div className="lg:col-span-3 space-y-8">
            <Link to="/" className="inline-block group">
              <img
                src="https://vcnrgroup.com/static/media/logo.a248d5f0fd9abb2a75015071670e6671.svg"
                className="h-12 transition-transform group-hover:scale-105"
                alt="VCNR Healthcare"
              />
            </Link>
            <p className="text-slate-500 text-base font-medium leading-relaxed italic">
              "Providing world-class tertiary healthcare through specialized
              clinical governance and human compassion."
            </p>
            <div className="flex gap-4">
              {[
                { icon: <Linkedin size={20} />, label: "LinkedIn" },
                { icon: <Instagram size={20} />, label: "Instagram" },
                { icon: <Facebook size={20} />, label: "Facebook" }
              ].map((social, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 hover:text-vcnr-brand hover:border-vcnr-brand hover:bg-vcnr-brand/5 transition-all"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Clinical Institutes (4/12) */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-5 bg-vcnr-brand rounded-full"></div>
              <h4 className="text-[12px] font-bold uppercase tracking-widest  text-slate-400 mb-1">
                Clinical Institutes
              </h4>
            </div>
            <ul className="space-y-4">
              {INSTITUTES.map(inst => (
                <li key={inst.id}>
                  <Link
                    to={`/institute/${inst.id}`}
                    className="group flex items-center justify-between text-sm md:text-base font-semibold text-slate-700 hover:text-vcnr-brand transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-200 group-hover:bg-vcnr-brand group-hover:scale-150 transition-all"></span>
                      {inst.name.replace("VCNR ", "")}
                     
                    </div>
                    <ChevronRight
                      size={14}
                      className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Hospital Hub (2/12) */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-5 bg-vcnr-brand rounded-full"></div>
              <h4 className="text-[12px] font-bold uppercase tracking-widest  text-slate-400 mb-1">
                Hospital Hub
              </h4>
            </div>
            <ul className="space-y-6">
              {[
                { label: "Home ", path: "/" },
                { label: "About VCNR", path: "/about" },
                { label: "Specialist Board", path: "/doctors" },
                { label: "Clinical Registry", path: "/booking" },
                { label: "Institutes Directory", path: "/institutes" }
              ].map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.path}
                    className="text-sm md:text-base font-semibold text-slate-800 hover:text-vcnr-brand transition-colors flex items-center gap-2 group"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Coordinates (3/12) */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-5 bg-vcnr-brand rounded-full"></div>
              <h4 className="text-[12px] font-bold uppercase tracking-widest  text-slate-400 mb-1">
                Coordinates
              </h4>
            </div>
            <div className="space-y-8">
              <div className="flex gap-4 group">
                <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-vcnr-brand shrink-0 group-hover:bg-vcnr-brand group-hover:text-white transition-all">
                  <MapPin size={24} />
                </div>
                <div>
                  <div className="text-[12px] font-bold uppercase tracking-widest  text-slate-400 mb-1">
                    Facility Hub
                  </div>
                  <address className="not-italic text-sm md:text-base  font-semibold text-slate-800 ">
                    VCNR Hospital, <br />
                    Opp Binnamangala Nursery, B.H. Road,
                    <br />
                    Nelamangala - 562123, Bangalore.
                  </address>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-vcnr-brand shrink-0">
                  <Phone size={24} />
                </div>
                <div>
                  <div className="text-[12px] font-black uppercase tracking-widest text-slate-400 mb-1">
                    24/7 Hotlines
                  </div>
                  <p className="text-base  font-semibold text-slate-800 tracking-tight">
                    +91 866 018 2095
                  </p>
                  <p className="text-base font-semibold text-slate-800 tracking-tight">
                    +91 916 430 4033
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-vcnr-brand shrink-0">
                  <Mail size={24} />
                </div>
                <div>
                  <div className="text-[12px] font-black  uppercase tracking-widest text-slate-400 mb-1">
                    Official Mail
                  </div>
                  <p className="text-sm  font-semibold text-slate-800 uppercase tracking-widest">
                    info@vcnrhealth.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

       

        {/* --- COMPLIANCE & LEGAL BOTTOM --- */}
        <div className="pt-12 pb-12 border-t border-slate-100 flex flex-col lg:flex-row justify-between items-center gap-8">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex items-center gap-3 text-[11px] font-black text-slate-400 uppercase tracking-[0.3em]">
              <Globe size={16} className="text-vcnr-brand" />
              &copy; {new Date().getFullYear()} VCNR HEALTHCARE GROUP / GLOBAL
              HUB
            </div>
            <div className="flex items-center gap-3 text-[11px] font-black text-slate-400 uppercase tracking-[0.3em]">
              <Award size={16} className="text-vcnr-brand" />
              NABH ACCREDITED TERTIARY CARE
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {[
              { label: "Regulatory Compliance", path: "#" },
              { label: "Privacy Standards", path: "#" },
              { label: "Clinical Ethics", path: "#" }
            ].map((link, i) => (
              <a
                key={i}
                href={link.path}
                className="text-[11px] font-black text-slate-400 uppercase tracking-widest hover:text-vcnr-brand transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Bottom Bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-vcnr-brand via-indigo-400 to-vcnr-brand"></div>
    </footer>
  )
}

export default Footer
